/**
 * Author - Brijesh Pandey
 * Git - https://github.com/brijesh-pandey/
 **/

import React, {useEffect, useRef, useState} from "react";
import {WealthyEnv, getAuthToken} from "helpers";
import styled, {css} from "styled-components";

import PropTypes from "prop-types";
import colors from "nxtconstants/colors";
import navLinks from "./constants";

const wealthyColoredLogo = `${WealthyEnv.HOMEPAGE_CDN_URL}/wl_colorful_logo.png`;

const propTypes = {
  allowSticky: PropTypes.bool,
  backgroundColor: PropTypes.string,
  navBackgroundColor: PropTypes.string,
  color: PropTypes.string,
  logo: PropTypes.string,
  isPartnerPage: PropTypes.bool,
  isHomePage: PropTypes.bool,
  showToggleButtons: PropTypes.bool
};

const defaultProps = {
  allowSticky: true,
  backgroundColor: 'transparent',
  navBackgroundColor: 'transparent',
  color: '#1c1c1c',
  logo: wealthyColoredLogo,
  isPartnerPage: false,
  isHomePage: false,
  showToggleButtons: true
};

const HeaderV2 = ({
  allowSticky,
  backgroundColor,
  navBackgroundColor,
  color,
  logo,
  isPartnerPage,
  isHomePage,
  showToggleButtons
}) => {
  const [sticky, setIsSticky] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showHeader, setShowHeader] = useState(true);
  const headerRef = useRef(null);
  const lastScrollY = useRef(0);
  const [currentPath, setCurrentPath] = useState('');
  const [isAuthTokenAvailable, setIsAuthTokenAvailable] = useState(null);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
    setIsAuthTokenAvailable(getAuthToken());
  }, []);

  // Check if a link is active
  const isActive = (pathname) => {
    if (!pathname || !currentPath) return false;

    if (currentPath === pathname) return true;

    if (pathname === '/partner' || pathname === '/investor') {
      if (currentPath === '/') return true;

      return false;
    }

    // For other paths, allow nested route matching
    return currentPath.startsWith(pathname + '/');
  };

  const isOnHomePage = isHomePage || currentPath === "/" || currentPath === "/homepage";
  const isPartnerHighlighted = !isOnHomePage && isPartnerPage;
  const isInvestorHighlighted = !isOnHomePage && !isPartnerPage;
  const currentNavLinks = navLinks.find((nav) => {
    if (isOnHomePage) {
      return nav.text === "HOMEPAGE";
    }

    return isPartnerPage ? nav.text === "PARTNERS" : nav.text === "INVESTORS";
  });

  // Handle sticky header on scroll - show on scroll up, hide on scroll down
  useEffect(() => {
    if (!allowSticky) return;

    let ticking = false;
    let lastUpdate = 0;

    const handleScroll = () => {
      const now = Date.now();

      // Throttle to max 60fps
      if (now - lastUpdate < 16) return;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          // Add threshold to prevent jittery behavior
          const scrollDifference = Math.abs(currentScrollY - lastScrollY.current);

          if (scrollDifference < 3) {
            ticking = false;
            return; // Ignore tiny scroll movements
          }

          // Determine scroll direction
          const isScrollingDown = currentScrollY > lastScrollY.current;
          const isScrollingUp = currentScrollY < lastScrollY.current;

          // Show header when scrolling up, hide when scrolling down
          if (currentScrollY > 80) {
            if (!sticky) setIsSticky(true);

            if (isScrollingUp && !showHeader) {
              setShowHeader(true);
            } else if (isScrollingDown && showHeader && currentScrollY > 150) {
              setShowHeader(false);
            }
          } else {
            // At the top of the page, always show header and not sticky
            if (sticky) setIsSticky(false);
            if (!showHeader) setShowHeader(true);
          }

          lastScrollY.current = currentScrollY;
          lastUpdate = now;
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, {passive: true});
    return () => window.removeEventListener('scroll', handleScroll);
  }, [allowSticky, sticky, showHeader]);

  // Handle mobile menu body scroll
  useEffect(() => {
    if (showMobileMenu) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showMobileMenu]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target) && !showMobileMenu) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMobileMenu]);

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  const handleSectionToggle = (section) => {
    if (section === 'partner') {
      window.location.href = `${WealthyEnv.URL}/partner`;
    } else {
      window.location.href = `${WealthyEnv.URL}/investor`;
    }
  };

  const getLoginUrl = () => {
    if (isPartnerPage || isOnHomePage) {
      return isAuthTokenAvailable ? WealthyEnv.BUILDWEALTH_APP_URL : '#login';
    } else {
      return isAuthTokenAvailable ? WealthyEnv.TESSERACT_BASE_URL :
        `${WealthyEnv.GATE_WEALTHY_API_URL}/wealthyauth/dashboard/login/?redirect_url=${WealthyEnv.TESSERACT_BASE_URL}`;
    }
  };

  const getSignupUrl = () => {
    if (isPartnerPage || isOnHomePage) {
      return '#register';
    } else {
      return `${WealthyEnv.GATE_WEALTHY_API_URL}/wealthyauth/dashboard/signup/`;
    }
  };

  const renderNavItem = (item, index) => {
    if (item.dropdownLinks) {
      const hasActiveChild = item.dropdownLinks.some(child => isActive(child.pathname));

      return (
        <NavItem key={index}>
          <DropdownTrigger
            onMouseEnter={() => setActiveDropdown(index)}
            onMouseLeave={() => setActiveDropdown(null)}
            isActive={activeDropdown === index}
            $isPageActive={hasActiveChild}
          >
            {item.text}
            <DropdownIcon src="https://i.wlycdn.com/wealthy-home-page/dropdown-header-icon.svg" alt="dropdown-icon" />
          </DropdownTrigger>
          <DropdownMenu
            isOpen={activeDropdown === index}
            onMouseEnter={() => setActiveDropdown(index)}
            onMouseLeave={() => setActiveDropdown(null)}
          >
            {item.dropdownLinks.map((dropdownItem, dropIndex) => (
              <DropdownItem key={dropIndex} $isActive={isActive(dropdownItem.pathname)}>
                <AssetIcon src={dropdownItem.icon} alt={`${dropdownItem.text}-icon`} />
                <DropdownLink href={dropdownItem.url} $isActive={isActive(dropdownItem.pathname)}>
                  {dropdownItem.text}
                </DropdownLink>
              </DropdownItem>
            ))}
          </DropdownMenu>
        </NavItem>
      );
    }

    return (
      <NavItem key={index}>
        <NavLink href={item.url} $isActive={isActive(item.pathname)}>
          {item.text}
        </NavLink>
      </NavItem>
    );
  };

  return (
    <>
      <HeaderSpacer />

      <div ref={headerRef}>
        <header>
          <HeaderInner
          $backgroundColor={backgroundColor}
          $color={color}
          $sticky={sticky}
          $showHeader={showHeader}
        >
        {/* Main Header Row */}
        <MainHeaderContent>
          {/* Logo */}
          <LogoContainer href={WealthyEnv.URL}>
            <Logo src={logo} alt="Wealthy Logo" />
          </LogoContainer>

          {/* Section Toggle Buttons */}
          {showToggleButtons && (
            <ToggleSection>
              <ToggleButton
                isActive={isPartnerHighlighted}
                onClick={() => handleSectionToggle('partner')}
              >
                <ActiveDot isActive={isPartnerHighlighted}>•</ActiveDot>
                For Mutual Fund Distributors
              </ToggleButton>
              <ToggleButton
                isActive={isInvestorHighlighted}
                onClick={() => handleSectionToggle('investor')}
              >
                <ActiveDot isActive={isInvestorHighlighted}>•</ActiveDot>
                For Investors
              </ToggleButton>
            </ToggleSection>
          )}

          {/* Auth Buttons */}
          <AuthSection>
            <LoginButton href={getLoginUrl()}>
              Login
            </LoginButton>
            <SignUpButton href={getSignupUrl()}>
              Sign Up
            </SignUpButton>
          </AuthSection>

          {/* Mobile Menu Button */}
          <MobileMenuButton onClick={() => setShowMobileMenu(!showMobileMenu)}>
            <span></span>
            <span></span>
            <span></span>
          </MobileMenuButton>
        </MainHeaderContent>

        {/* Navigation Row */}
        <NavigationRow $navBackgroundColor={navBackgroundColor}>
          <NavigationContent>
            <DesktopNav>
              <NavList>
                {currentNavLinks?.subLinks?.filter(item => item.isMainNav)?.map((item, index) => renderNavItem(item, index))}
              </NavList>
            </DesktopNav>
          </NavigationContent>
        </NavigationRow>
          </HeaderInner>
        </header>
      </div>

      {/* Mobile Menu - Outside header to avoid transform issues */}
      <MobileMenu isOpen={showMobileMenu}>
        <MobileHeader>
          <div></div>
          <MobileCloseButton onClick={() => setShowMobileMenu(false)}>
            ✕
          </MobileCloseButton>
        </MobileHeader>

        <MobileNavList>
          {showToggleButtons && (
            <MobileToggleSection>
              <MobileToggleButton
                isActive={isPartnerHighlighted}
                onClick={() => handleSectionToggle('partner')}
              >
                <ActiveDot isActive={isPartnerHighlighted}>•</ActiveDot>
                For Mutual Fund Distributors
              </MobileToggleButton>
              <MobileToggleButton
                isActive={isInvestorHighlighted}
                onClick={() => handleSectionToggle('investor')}
              >
                <ActiveDot isActive={isInvestorHighlighted}>•</ActiveDot>
                For Investors
              </MobileToggleButton>
            </MobileToggleSection>
          )}

          {currentNavLinks?.subLinks?.filter(item => item.isMainNav)?.map((item, index) => {
            const hasActiveChild = item.dropdownLinks?.some(child => isActive(child.pathname));

            return (
              <MobileNavItem key={index}>
                {item.dropdownLinks ? (
                  <>
                    <MobileDropdownTrigger
                      onClick={() => handleDropdownToggle(`mobile-${index}`)}
                      $isPageActive={hasActiveChild}
                    >
                      {item.text}
                      <MobileDropdownIcon
                        src="https://i.wlycdn.com/wealthy-home-page/dropdown-header-icon.svg"
                        alt="dropdown-icon"
                        isOpen={activeDropdown === `mobile-${index}`}
                      />
                    </MobileDropdownTrigger>
                    {activeDropdown === `mobile-${index}` && (
                      <MobileDropdownList>
                        {item.dropdownLinks.map((dropdownItem, dropIndex) => (
                          <MobileDropdownItem key={dropIndex} $isActive={isActive(dropdownItem.pathname)}>
                            <MobileAssetIcon src={dropdownItem.icon} alt={`${dropdownItem.text}-icon`} />
                            <MobileDropdownLink href={dropdownItem.url} $isActive={isActive(dropdownItem.pathname)}>
                              {dropdownItem.text}
                            </MobileDropdownLink>
                          </MobileDropdownItem>
                        ))}
                      </MobileDropdownList>
                    )}
                  </>
                ) : (
                  <MobileNavLink href={item.url} $isActive={isActive(item.pathname)}>
                    {item.text}
                  </MobileNavLink>
                )}
              </MobileNavItem>
            );
          })}

          <MobileAuthSection>
            <MobileLoginButton href={getLoginUrl()} onClick={() => setShowMobileMenu(false)}>
              Login
            </MobileLoginButton>
            <MobileSignUpButton href={getSignupUrl()} onClick={() => setShowMobileMenu(false)}>
              Sign Up
            </MobileSignUpButton>
          </MobileAuthSection>
        </MobileNavList>
      </MobileMenu>
    </>
  );
};

// Styled Components
const HeaderSpacer = styled.div`
  height: 120px;
  pointer-events: none;

  @media (max-width: 768px) {
    height: 60px;
  }
`;

const HeaderInner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 998;
  background-color: ${props => props.$sticky ? colors.white : props.$backgroundColor || 'transparent'};
  color: ${props => props.$color};
  transition: background-color 0.3s ease,
              box-shadow 0.3s ease,
              transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.$sticky && props.$showHeader ? `0 2px 8px rgba(0, 0, 0, 0.08)` : 'none'};
  font-family: 'Lato', sans-serif;
  transform: ${props => {
    const translateY = props.$sticky ? (props.$showHeader ? '0' : '-100%') : '0';
    return `translate3d(0, ${translateY}, 0)`;
  }};
  will-change: transform;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  perspective: 1000px;
  pointer-events: ${props => props.$sticky && !props.$showHeader ? 'none' : 'auto'};
`;

const MainHeaderContent = styled.div`
  display: flex;
  align-items: center;
  padding: 3rem 0;
  height: 60px;
  max-width: 125rem;
  width: 85%;
  margin: 0 auto;

  @media (max-width: 1200px) {
    padding: 2rem;
  }

  @media (max-width: 830px) {
    margin: 0.25rem;
  }

  @media (max-width: 768px) {
    padding: 0rem;
    height: 60px;
    max-width: 125rem;
    width: 85%;
    margin: 0 auto;
    justify-content: space-between;
  }
`;

const NavigationRow = styled.div`
  border-top: 1px solid #E4E4E4;
  background: ${props => props.$navBackgroundColor};

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavigationContent = styled.div`
  max-width: 125rem;
  margin: 0 auto;
  width: 85%;
  padding: 1rem 0rem;

  @media (max-width: 1200px) {
    padding: 1rem 2rem;
  }
`;

const LogoContainer = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  margin-right: 6rem;

  @media (max-width: 1024px) {
    margin-right: 3rem;
  }
`;

const Logo = styled.img`
  height: 32px;
  width: auto;

  @media (max-width: 768px) {
    height: 28px;
  }
`;

const ToggleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 3rem;
  margin-right: 6rem;

  @media (max-width: 1024px) {
    gap: 2rem;
    margin-right: 3rem;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  font-size: 1.6rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => props.isActive ? colors.primary : '#1c1c1c'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 1024px) {
    font-size: 1.5rem;
  }
`;

const ActiveDot = styled.span`
  color: ${props => props.isActive ? colors.primary : 'transparent'};
  font-size: 1.6rem;
  line-height: 1;
`;

const DesktopNav = styled.nav`
  display: flex;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  justify-content: center;
  list-style: none;
  margin: 0;
  padding: 1.2rem 0;
  gap: 5rem;

  @media (max-width: 1024px) {
    gap: 3rem;
  }
`;

const NavItem = styled.li`
  position: relative;
`;

const NavLink = styled.a`
  font-size: 1.6rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => props.$isActive ? colors.primary : '#1c1c1c'};
  text-decoration: none;
  transition: color 0.2s ease;
  white-space: nowrap;
  /* text-transform: uppercase; */
  letter-spacing: 0.5px;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 1024px) {
    font-size: 1.5rem;
  }
`;

const DropdownTrigger = styled.div`
  font-size: 1.6rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => (props.isActive || props.$isPageActive) ? colors.primary : '#1c1c1c'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: color 0.2s ease;
  white-space: nowrap;
  /* text-transform: uppercase; */
  letter-spacing: 0.5px;

  &:hover {
    color: ${colors.primary};
  }

  @media (max-width: 1024px) {
    font-size: 1.5rem;
  }
`;

const DropdownIcon = styled.img`
  width: 1.2rem;
  height: 1.2rem;
  opacity: 0.7;
  margin-left: 0.5rem;
`;

const DropdownMenu = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: ${colors.white};
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  list-style: none;
  margin: 0;
  padding: 1.5rem;
  min-width: 220px;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: translateY(${props => props.isOpen ? '0' : '-8px'});
  transition: all 0.2s ease;
  z-index: 1001;
  border: 1px solid rgba(0, 0, 0, 0.06);
`;

const DropdownItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1rem 0;
  border: 1px solid ${props => props.$isActive ? colors.primary : '#EAEAEA'};
  border-radius: 1.6rem;
  opacity: ${props => props.$isActive ? '1' : '0.8'};
  text-decoration: none;
  font-weight: 400;

  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  &:hover {
    opacity: 1;
    font-weight: 700;
    border-color: ${colors.primary};
  }
`;

const DropdownLink = styled.a`
  display: block;
  padding: 0.8rem 1.6rem;
  font-size: 1.4rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => props.$isActive ? colors.primary : '#1c1c1c'};
  text-decoration: none;
  transition: all 0.2s ease;
`;

const AssetIcon = styled.img`
  width: 3.3rem;
  height: 3.3rem;
  margin-left: 1.2rem;
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-left: auto;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LoginButton = styled.a`
  font-size: 1.6rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${colors.primary};
  text-decoration: none;
  transition: all 0.2s ease;
  background: ${colors.white};
  padding: 0.6rem 4rem;
  border-radius: 100px;
  /* border: 1px solid #d0d0d0; */

  &:hover {
    color: ${colors.primary};
    border-color: ${colors.primary};
  }
`;

const SignUpButton = styled.a`
  background: ${colors.primary};
  color: ${colors.white};
  padding: 0.6rem 4rem;
  border-radius: 100px;
  font-size: 1.6rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;
  /* text-transform: uppercase; */

  &:hover {
    background: ${colors.white};
    color: ${colors.primary};
    transform: translateY(-1px);
    /* box-shadow: 0 4px 12px rgba(103, 37, 244, 0.3); */
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  flex-direction: column;
  gap: 5px;
  cursor: pointer;
  padding: 8px;
  position: relative;
  z-index: 999;

  span {
    width: 26px;
    height: 3px;
    background: #1c1c1c;
    transition: all 0.3s ease;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.white};
  transform: translateX(${props => props.isOpen ? '0' : '100%'});
  transition: transform 0.3s ease;
  z-index: 1000;
  overflow-y: auto;
  display: block;

  @media (min-width: 769px) {
    display: none;
  }
`;

const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #f0f0f0;
  position: sticky;
  top: 0;
  background: ${colors.white};
  z-index: 10;
`;

const MobileLogo = styled.img`
  height: 2.8rem;
  width: auto;
`;

const MobileCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2.4rem;
  color: #1c1c1c;
  cursor: pointer;
  padding: 0.5rem;
  font-family: 'Lato', sans-serif;
`;

const MobileNavList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 2rem;
`;

const MobileToggleSection = styled.li`
  list-style: none;
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #f0f0f0;
`;

const MobileToggleButton = styled.button`
  flex: 1;
  background: ${props => props.isActive ? colors.primary : '#F9F6FF'};
  border: 1px solid ${props => props.isActive ? colors.primary : colors.primary};
  padding: 1.2rem 1.6rem;
  border-radius: 100px;
  font-size: 1.2rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => props.isActive ? colors.white : colors.primary};
  text-align: center;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.isActive ? colors.luckyPoint : '#F0F0FF'};
  }
`;

const MobileNavItem = styled.li`
  margin-bottom: 2.5rem;
`;

const MobileNavLink = styled.a`
  display: block;
  padding: 1rem 0;
  font-size: 1.8rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => props.$isActive ? colors.primary : '#1c1c1c'};
  text-decoration: none;

  &:hover {
    color: ${colors.primary};
  }
`;

const MobileDropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 1rem 0;
  font-size: 1.8rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => props.$isPageActive ? colors.primary : '#1c1c1c'};
  text-align: left;
  cursor: pointer;

  &:hover {
    color: ${colors.primary};
  }
`;

const MobileDropdownList = styled.ul`
  list-style: none;
  margin: 1rem 0 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const MobileDropdownItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  /* justify-content: center; */
  gap: 2rem;
  padding: 1rem;
  background-color: ${colors.white};
  border: 1px solid ${props => props.$isActive ? colors.primary : '#EAEAEA'};
  border-radius: 1rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.primary};
    transform: translateY(-2px);
  }
`;

const MobileDropdownIcon = styled.img`
  width: 1.2rem;
  height: 1.2rem;
  opacity: 0.7;
  margin-left: 0.5rem;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
`;

const MobileAssetIcon = styled.img`
  width: 4rem;
  height: 4rem;
`;

const MobileDropdownLink = styled.a`
  font-size: 1.4rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${props => props.$isActive ? colors.primary : '#1c1c1c'};
  text-decoration: none;
  text-align: center;
`;

const MobileAuthSection = styled.li`
  list-style: none;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #f0f0f0;
  display: flex;
  gap: 1rem;
`;

const MobileLoginButton = styled.a`
  flex: 1;
  padding: 1.2rem 0;
  font-size: 1.6rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  color: ${colors.primary};
  text-decoration: none;
  text-align: center;
  background: ${colors.white};
  border: 1px solid #d0d0d0;
  border-radius: 100px;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.primary};
  }
`;

const MobileSignUpButton = styled.a`
  flex: 1;
  background: ${colors.primary};
  color: ${colors.white};
  padding: 1.2rem 0;
  border-radius: 100px;
  font-size: 1.6rem;
  font-weight: 400;
  font-family: 'Lato', sans-serif;
  text-decoration: none;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.luckyPoint};
  }
`;

HeaderV2.propTypes = propTypes;
HeaderV2.defaultProps = defaultProps;

export default HeaderV2;
